import axios from "axios";
import { ApiError } from "../../../../lib/api-client";

// Forex is a separate backend (own host, own admin key) — it does not share
// lib/api-client's instance, which carries the Gidex baseURL + OMNISERVE key.
//
// ponytail: the key is in the browser bundle. Stage only. Before production this
// must move behind a proxy on our own backend (or the forex API grows real admin
// auth) — swap BASE for that proxy route and drop the header, nothing else changes.
const forexClient = axios.create({
  baseURL:
    process.env.REACT_APP_FOREX_API_BASE_URL || "https://forexstage.ginox.io",
  headers: {
    "x-admin-key": process.env.REACT_APP_FOREX_ADMIN_KEY || "",
    "Content-Type": "application/json",
  },
  timeout: 20000,
});

forexClient.interceptors.response.use(
  (r) => r,
  (error) => {
    const body = error.response?.data;
    // 429 breaks the envelope: bare { error: "Too many requests" }.
    const message =
      body?.message || body?.error || error.message || "Request failed";
    return Promise.reject(
      new ApiError(error.response?.status ?? 0, message, body),
    );
  },
);

async function unwrap(promise) {
  const { data: envelope, status } = await promise;
  if (!envelope?.success) {
    throw new ApiError(
      envelope?.statusCode ?? status,
      envelope?.message ?? "Request failed",
      envelope,
    );
  }
  return envelope.data;
}

// ── Withdrawals ────────────────────────────────────────────────
//
// Unlike the verification queue this one is a BARE ARRAY with no limit, no cursor
// and no total: every SUBMITTED row, oldest first. Nothing to paginate.
const WITHDRAWALS = "/api/v1/forex/withdrawals/admin";

export const fetchWithdrawalQueue = () => unwrap(forexClient.get(`${WITHDRAWALS}/queue`));

// Records a payment the admin ALREADY made by hand. Moves no money and takes no
// transaction hash — it only flips the state and emails the user. 409 = another
// admin got there first.
export const markWithdrawalSent = (id) =>
  unwrap(forexClient.post(`${WITHDRAWALS}/${id}/sent`, {}));

// The only admin call here that DOES move money: it credits the full amount back to
// the MT5 account. No reason is accepted and none is recorded.
//
// On 502/503 the row is already DECLINED but the refund is still owed, and calling
// decline again retries only the credit. No endpoint lists declined rows, so such a
// row cannot be reached from the console — see the note in the queue page.
export const declineWithdrawal = (id) =>
  unwrap(forexClient.post(`${WITHDRAWALS}/${id}/decline`, {}));

// ── Deposits (admin) ───────────────────────────────────────────
//
// THREE different envelope shapes live under this one prefix, so nothing here
// shares a parser:
//   pending / not-credited  bare array
//   uncollected             { deposits, totals }
//   collections (GET)       { entries, nextCursor, totals }
const DEPOSITS = "/api/v1/forex/deposits/admin";

// Money arrived but the MT5 credit did not happen. `arrivedAt` can be null.
export const fetchPendingDeposits = () => unwrap(forexClient.get(`${DEPOSITS}/pending`));

// Runs the exact credit the webhook would have run. 409 = no longer pending.
export const creditDeposit = (id) =>
  unwrap(forexClient.post(`${DEPOSITS}/${id}/credit`, {}));

// Paid late or below the minimum, so it will never be credited. Each row carries
// its arrivedPayments, which is the evidence an operator needs to refund by hand.
export const fetchNotCreditedDeposits = () =>
  unwrap(forexClient.get(`${DEPOSITS}/not-credited`));

// Records that a refund was made OUTSIDE the system. Moves no money itself.
export const refundDeposit = (id) =>
  unwrap(forexClient.post(`${DEPOSITS}/${id}/refund`, {}));

// Funded addresses waiting to be swept, plus both totals.
//
// Reads a live chain balance PER ROW and has no pagination, so it is slow and gets
// slower as funded addresses accumulate. Never poll it.
export const fetchUncollected = () => unwrap(forexClient.get(`${DEPOSITS}/uncollected`));

// Records a sweep from its hash. The backend reads the receipt itself and writes
// only what the events prove, so nothing here is trusted except the hash.
// Re-posting the same hash is harmless: it writes nothing and answers written: 0.
export const recordCollection = (txHash) =>
  unwrap(forexClient.post(`${DEPOSITS}/collections`, { txHash }));

// Sweep history, newest first. The one admin route that paginates.
export const fetchCollections = ({ limit = 50, cursor } = {}) =>
  unwrap(
    forexClient.get(`${DEPOSITS}/collections`, {
      params: cursor ? { limit, cursor } : { limit },
    }),
  );
